/**
 * Fetch + derive the checklist sub-steps for one STANDALONE harness task
 * (sidebar redesign 2026-07-19, handoff §4 option (a)).
 *
 * Lazily fetches `GET /harn/tasks/:id/logs?type=progress` only while the
 * row is expanded, then re-fetches whenever the task's latest progress
 * frame changes (`progressKey`) so an expanded, running harness keeps
 * its list current without a poller of its own.
 *
 * Fail-soft by design: any error yields an EMPTY step list and a `false`
 * error flag on the row rather than a broken row. Sub-steps are an
 * enrichment — the harness row was already meaningful without them, and
 * a deployment with no log sink (`HARN_LOG_DIR` unset) legitimately
 * returns nothing.
 */
import { useEffect, useRef, useState } from "react";

import { getTaskLogs, type HarnTaskStatus } from "@/api/lx-harn";
import {
  deriveHarnessSteps,
  type HarnessStep,
} from "@/components/team-of-agents/harness-steps";

export interface UseHarnessStepsResult {
  steps: HarnessStep[];
  isLoading: boolean;
}

export function useHarnessSteps(
  taskId: string,
  status: HarnTaskStatus,
  enabled: boolean,
  /** Changes whenever the task reports a new progress frame; drives the
   *  re-fetch of an expanded row. */
  progressKey?: string,
): UseHarnessStepsResult {
  const [steps, setSteps] = useState<HarnessStep[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Guards against a late response mutating state after unmount/collapse.
  const disposedRef = useRef(false);

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setIsLoading(true);
    void (async () => {
      try {
        const entries = await getTaskLogs(taskId, { type: "progress" });
        if (cancelled || disposedRef.current) return;
        setSteps(deriveHarnessSteps(entries, { status }));
      } catch {
        // Enrichment-only: render the row with no children.
        if (!cancelled && !disposedRef.current) setSteps([]);
      } finally {
        if (!cancelled && !disposedRef.current) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [taskId, status, enabled, progressKey]);

  return { steps, isLoading };
}
